import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  Search,
  Plus,
  Users,
  Phone,
  Mail,
  MapPin,
  ChevronRight,
  User,
  ChevronLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const ClientsPage = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState(
    {
      rows: [],
      page: 1,
      totalRow: 0,
      totalPages: 0
    }
  );
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    rnc: "",
    assigned_to: "",
  });
  const [saving, setSaving] = useState(false);


  //Terminos de Consultas
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentSucursal, setCurrentSucursal] = useState(-1);
  const [currentEstado, setCurrentEstado] = useState(1);

  useEffect(() => {
    fetchClients();
  }, []);

  const searchTermfilter = searchTerm.trim();



  useEffect(() => {
    fetchClients();
    //eslint-disable-next-line
  }, [searchTermfilter, rowsPerPage, currentPage, currentSucursal, currentEstado]);


  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          search: searchTermfilter,
          page: currentPage,
          cantidadRow: rowsPerPage,
          sucursal: currentSucursal,
          estado: currentEstado
        }
      });
      setClients(response.data);
    } catch (error) {
      toast.error("Error al cargar clientes");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = { ...formData };
      if (!data.assigned_to) {
        data.assigned_to = null;
      }
      await axios.post(`${API}/clients`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Cliente creado exitosamente");
      setShowModal(false);
      setFormData({ name: "", email: "", phone: "", address: "", rnc: "", assigned_to: "" });
      fetchClients();
    } catch (error) {
      toast.error("Error al crear cliente");
    } finally {
      setSaving(false);
    }
  };



  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const refreshClients = () => {
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">Lista de Clientes</h2>
          <p className="text-sm text-slate-500">{clients.length} clientes registrados</p>
        </div>

      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
        <Input
          data-testid="client-search"
          placeholder="Buscar por nombre, email o RNC..."
          value={searchTerm}
          onChange={(e) => {
            setCurrentPage(1);
            setSearchTerm(e.target.value)
          }}
          className="pl-10 h-11"
        />
      </div>

      {/* Client Grid */}
      {clients.rows.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <Users size={32} className="text-slate-400" />
          </div>
          <h3 className="text-lg font-medium text-slate-800 mb-2">
            No hay clientes
          </h3>
          <p className="text-slate-500 mb-4">
            {searchTerm
              ? "No se encontraron clientes con ese criterio"
              : "Comienza agregando tu primer cliente"}
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children"  >
            {clients.rows.map((client) => (
              <div
                key={client.id}
                data-testid={`client-card-${client.id}`}
                onClick={() => navigate(`/clients/${client.id}`)}
                className="client-card hover-lift"

              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded bg-slate-100 flex items-center justify-center">
                      <span className="text-sm font-semibold text-slate-600">
                        {client.name
                          .split(" ")
                          .map((n) => n[0])
                          .join("")
                          .slice(0, 2)
                          .toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-800">{client.name}</h3>
                      {client.rnc && (
                        <p className="text-xs text-slate-500 mono">RNC: {client.rnc}</p>
                      )}
                    </div>
                  </div>
                  <ChevronRight size={18} className="text-slate-400" />
                </div>

                <div className="space-y-2 text-sm" style={{ minHeight: 80 }}>
                  {client.email && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Mail size={14} className="text-slate-400" />
                      <span className="truncate">{client.email}</span>
                    </div>
                  )}
                  {client.phone && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Phone size={14} className="text-slate-400" />
                      <span>{client.phone}</span>
                    </div>
                  )}
                  {client.address && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <MapPin size={14} className="text-slate-400" />
                      <span className="truncate">{client.address}</span>
                    </div>
                  )}
                </div>

                {client.assigned_to_name && (
                  <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
                    <User size={12} className="text-slate-400" />
                    <span>Asignado a: {client.assigned_to_name}</span>
                  </div>
                )}

                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
                  <span className="text-xs text-slate-500">Balance:</span>
                  <span
                    className={`font-semibold mono ${client.balance > 0 ? "text-amber-600" : "text-emerald-600"
                      }`}
                  >
                    RD$ {client.balance?.toLocaleString("es-DO", { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            ))}
          </div>
          {/* Pagination */}
          {/* {clients.totalPages > 1 && ( */}

          < div className="flex items-center justify-between mt-8 p-4 bg-white rounded-lg border border-slate-200">

            <div className="text-sm text-slate-500">
              Mostrando {((clients.page - 1) * clients.totalPages) + 1} - {clients.rows.length} de {clients.totalRow} clientes
            </div>
            <div className="flex items-center gap-1">
              {/* Previous Button */}
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => handlePageChange(clients.page - 1)}
                disabled={clients.page === 1}
              >
                <ChevronLeft size={16} />
              </Button>

              {/* Page Numbers */}
              {(() => {
                const pages = [];
                const totalPages = clients.totalPages;
                const currentPage = clients.page;

                // Always show first page
                pages.push(1);

                // Calculate range around current page
                let startPage = Math.max(2, currentPage - 1);
                let endPage = Math.min(totalPages - 1, currentPage + 1);

                // Add ellipsis after first page if needed
                if (startPage > 2) {
                  pages.push('...');
                }

                // Add pages in range
                for (let i = startPage; i <= endPage; i++) {
                  if (!pages.includes(i)) {
                    pages.push(i);
                  }
                }

                // Add ellipsis before last page if needed
                if (endPage < totalPages - 1) {
                  pages.push('...');
                }

                // Always show last page if more than 1 page
                if (totalPages > 1 && !pages.includes(totalPages)) {
                  pages.push(totalPages);
                }

                return pages.map((page, index) => (
                  page === '...' ? (
                    <span key={`ellipsis-${index}`} className="px-2 text-slate-400">...</span>
                  ) : (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      size="icon"
                      className={`h-8 w-8 ${currentPage === page ? 'bg-[#0F172A] text-white' : ''}`}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </Button>
                  )
                ));
              })()}

              {/* Next Button */}
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => handlePageChange(clients.page + 1)}
                disabled={!clients.page === clients.totalPages}
              >
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
          {/* )} */}
        </>

      )
      }


    </div >
  );
};

export default ClientsPage;
